import React, { useContext, useEffect, useState } from 'react'
import api from "../../services/api"
import Select from "react-select"
import Swal from "sweetalert2"
import { MainContext } from "../../context/MainContext"

interface Props {
    athleteId: string | undefined
}

export default function AthleteEventTypesForm({ athleteId }: Props) {
    const { club } = useContext(MainContext)
    const [athleteEventTypes, setAthleteEventTypes] = useState<any[]>([])
    const [eventTypes, setEventTypes] = useState<any[]>([])
    const [categories, setCategories] = useState<any[]>([])
    const [selectedEventType, setSelectedEventType] = useState<any>(null)
    const [selectedCategory, setSelectedCategory] = useState<any>(null)
    const [editingId, setEditingId] = useState<number | null>(null)

    useEffect(() => {
        if (athleteId) {
            loadAthleteEventTypes()
            loadEventTypes()
        }
    }, [athleteId])

    async function loadAthleteEventTypes() {
        const response = await api.get(`/api/athletes/${athleteId}`)
        setAthleteEventTypes(response.data.athleteEventTypes || [])
    }

    async function loadEventTypes() {
        const response = await api.get('/api/eventTypes?has_ranking=1')
        setEventTypes(response.data.map((et: any) => ({
            value: et.id,
            label: et.description
        })))
    }

    async function handleEventTypeChange(option: any) {
        setSelectedEventType(option)
        setSelectedCategory(null)
        
        if (option) {
            const response = await api.get(`/api/event-categories?event_type_id=${option.value}`)
            setCategories(response.data.map((cat: any) => ({
                value: cat.id,
                label: cat.category_name
            })))
        }
    }

    async function handleAdd() {
        if (!selectedEventType || !selectedCategory) {
            await Swal.fire({
                title: 'Atenção!',
                text: 'Selecione o tipo de prova e a categoria!',
                icon: 'warning',
            })
            return
        }

        try {
            if (editingId) {
                await api.put(`/api/athlete_event_types/${editingId}`, {
                    event_category_id: selectedCategory.value
                })
                await Swal.fire({
                    title: 'Sucesso!',
                    text: 'Categoria atualizada!',
                    icon: 'success',
                    timer: 2000
                })
                setEditingId(null)
            } else {
                await api.post('/api/athlete_event_types', {
                    athlete_id: athleteId,
                    event_type_id: selectedEventType.value,
                    event_category_id: selectedCategory.value,
                    club_id: club?.id || 1
                })
                await Swal.fire({
                    title: 'Sucesso!',
                    text: 'Categoria adicionada!',
                    icon: 'success',
                    timer: 2000
                })
            }

            setSelectedEventType(null)
            setSelectedCategory(null)
            loadAthleteEventTypes()
        } catch (error: any) {
            await Swal.fire({
                title: 'Erro!',
                text: error.response?.data?.message || 'Erro ao salvar categoria',
                icon: 'error',
            })
        }
    }

    async function handleEdit(aet: any) {
        setEditingId(aet.id)
        const eventType = eventTypes.find(et => et.value === aet.event_type_id)
        setSelectedEventType(eventType)
        
        const response = await api.get(`/api/event-categories?event_type_id=${aet.event_type_id}`)
        const cats = response.data.map((cat: any) => ({
            value: cat.id,
            label: cat.category_name
        }))
        setCategories(cats)
        setSelectedCategory(cats.find((c: any) => c.value === aet.event_category_id))
    }

    async function handleDelete(id: number) {
        const result = await Swal.fire({
            title: 'Tem certeza?',
            text: 'Deseja remover esta categoria?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim',
            cancelButtonText: 'Cancelar'
        })

        if (result.isConfirmed) {
            try {
                await api.delete(`/api/athlete_event_types/${id}`)
                await Swal.fire({
                    title: 'Sucesso!',
                    text: 'Categoria removida!',
                    icon: 'success',
                    timer: 2000
                })
                loadAthleteEventTypes()
            } catch (error) {
                await Swal.fire({
                    title: 'Erro!',
                    text: 'Erro ao remover categoria',
                    icon: 'error',
                })
            }
        }
    }

    if (!athleteId) return null

    return (
        <>
            <h3>Categorias das Provas</h3>
            <div className="card">
                <div className="card-body">
                    <div className="row g-3 mt-2">
                        <div className="col-md-5">
                            <label className="form-label">Tipo de Prova</label>
                            <Select
                                options={eventTypes}
                                value={selectedEventType}
                                onChange={handleEventTypeChange}
                                placeholder="Selecione o tipo de prova"
                            />
                        </div>
                        <div className="col-md-5">
                            <label className="form-label">Categoria</label>
                            <Select
                                options={categories}
                                value={selectedCategory}
                                onChange={setSelectedCategory}
                                placeholder="Selecione a categoria"
                                isDisabled={!selectedEventType}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button type="button" className="btn btn-primary" onClick={handleAdd}>
                                {editingId ? 'Atualizar' : 'Adicionar'}
                            </button>
                            {editingId && (
                                <button type="button" className="btn btn-secondary ms-2" onClick={() => {
                                    setEditingId(null)
                                    setSelectedEventType(null)
                                    setSelectedCategory(null)
                                }}>
                                    Cancelar
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="mt-4">
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th>Tipo de Prova</th>
                                    <th>Categoria</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {athleteEventTypes.length > 0 ? (
                                    athleteEventTypes.map((aet: any) => (
                                        <tr key={aet.id}>
                                            <td>{aet.eventType?.description}</td>
                                            <td>{aet.eventCategory?.category_name}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-warning me-2"
                                                    onClick={() => handleEdit(aet)}
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleDelete(aet.id)}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={3} className="text-center">
                                            Nenhuma categoria cadastrada
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    )
}
